<template>

    <div>
        <!-- Remove padding around component if used as selector -->
        <v-container fluid  :class="selectOnly ? 'pa-0' : 'pa-3'" v-if="sessionActive">

            <material-header
                    v-show="!selectOnly"
                    title="Segments"
                    info="Create segments of panelists, filters and experiment in real time"
                    help="segments"
                    icon="mdi-target">

                <template slot="buttons">

                    <!-- RELOAD BUTTON -->
                    <v-tooltip left color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn
                                    v-on="on"
                                    icon
                                    @click="loadSegments(true,true)"
                                    text>
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>

                        <span>Reload all segments</span>
                    </v-tooltip>

                    <!-- CREATE BUTTON -->
                    <v-tooltip top color="primary" v-if="!selectOnly">
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission('create queries')"
                                   v-on="on"
                                   fab
                                   small
                                   class="elevation-0 ml-2"
                                   color="primary"
                                   @click="segmentCreate()">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        <span>Create new segment</span>
                    </v-tooltip>

                    <!-- CANCEL SELECTION BUTTON -->
                    <v-tooltip left color="primary" v-if="selectOnly">
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission('create queries')"
                                   v-on="on"
                                   icon
                                   text
                                   @click="segmentClosed()">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Cancel selection</span>
                    </v-tooltip>

                </template>

            </material-header>

            <v-alert v-if="!hasPermission('run queries')"
                     class="body-2"
                     type="error"
            >
                Your user account doesn't have permission to access segments
            </v-alert>

            <v-tabs color="white" slider-color="primary" v-if="hasPermission('run queries')" v-model="activeTab">

                <v-tab class="ml-3" key="my-sample-definitions">
                    My segments
                </v-tab>

                <v-tab key="shared-sample-definitions">
                    Shared segments
                </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
                <v-tab-item key="my-sample-definitions">
                    <my-segments
                            :select-only="selectOnly"
                            :filters-only="filtersOnly"
                            @edit="segmentEdit"
                            @load="loadSegments(true,true)"
                            @select="segmentSelected"
                    ></my-segments>
                </v-tab-item>

                <v-tab-item key="shared-sample-definitions">
                    <shared-segments
                            :select-only="selectOnly"
                            :filters-only="filtersOnly"
                            @edit="segmentEdit"
                            @load="loadSegments(true,true)"
                            @select="segmentSelected"
                    ></shared-segments>
                </v-tab-item>
            </v-tabs-items>

        </v-container>

        <!-- SEGMENT BUILDER DIALOG -->
        <v-dialog
                v-model="builderOpenModel"
                fullscreen
                hide-overlay
                transition="fade-transition"
        >
            <v-card v-if="builderOpenModel" color="grey darken-4">
                <v-card-text>

                    <segment-builder
                            :key="builderKey"
                            @cancel="cancelSegment"
                            @save="saveSegmentDo"
                            v-bind="segment"
                            :saving="segmentSaving"
                            :errorMessages="errorMessages"
                            :saveButtonText="saveButtonText"
                    ></segment-builder>

                </v-card-text>
            </v-card>

        </v-dialog>

    </div>

</template>

<script>

    import Store from '../store';
    import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
    import SegmentBuilder from './SegmentBuilder';
    import MySegments from './MySegments';
    import SharedSegments from './SharedSegments';
    import {mapActions, mapState} from 'vuex';
    import Form from '../../../components/forms/Form';
    import MaterialHeader from '../../../components/UI/MaterialHeader';
    import {v4 as uuid} from 'uuid';

    export default {

        mixins: [activeSessionChecker],

        components: {
            MaterialHeader,
            SegmentBuilder,
            MySegments,
            SharedSegments,
        },

        props: {

            /** Select only mode used by SegmentPickerDialog **/
            selectOnly: {
                required: false,
                type: Boolean,
                default: false,
            },

            /** Segment filters only used by SegmentPickerDialog **/
            filtersOnly: {
                required: false,
                type: Boolean,
                default: false,
            }
        },

        data() {
            return {
                builderKey: null,
                activeTab: null,
                previousTab: null,
                builderOpenModel: false,
                segment: null,
                errorMessages: {},
                saveButtonText: 'SAVE'
            }
        },

        computed: {

            // Root store
            ...mapState([
                'user'
            ]),

            ...mapState('Segments', [
                'segmentSaving'
            ])
        },

        methods: {

            // Root actions
            ...mapActions([
                'getDataVariables',
            ]),

            ...mapActions('Segments', [
                'getSegments',
                'saveSegment',
                'updateSegment'
            ]),

            changeTabTo(tabNumber) {
                this.previousTab = this.activeTab;
                this.activeTab = tabNumber;
            },

            segmentCreate() {
                this.builderKey = uuid();
                this.segment = {
                    id: null,
                    json_query: {
                        "booleanOperation":"must"
                    },
                    json_filter: {},
                };
                this.builderOpenModel = true;
                this.saveButtonText = "SAVE";
            },

            segmentSelected(segment) {
                this.$emit('select', segment);
            },

            saveSegmentSuccess() {
                this.segment = null;
                this.builderOpenModel = false;
                this.changeTabTo(0);
            },

            cancelSegment() {
                this.segment = null;
                this.builderOpenModel = false;
            },

            segmentEdit(segment) {
                this.builderKey = uuid();
                this.segment = segment;
                this.builderOpenModel = true;

                if (this.segment.created_by.id === this.user.id) {
                    this.saveButtonText = "UPDATE";
                } else {
                    this.saveButtonText = "COPY";
                }
            },

            loadSegments(notification = false, force = false) {
                this.getSegments(force)
                    .then(() => {
                        if (notification) Bus.$emit('flash-message', {message: 'Segments updated'});
                    })
                    .catch(() => {
                        Bus.$emit('server-error');
                    });
            },

            saveSegmentDo(segment) {

                if (!segment.id) {
                    this.createSegmentDo(segment);
                } else if (this.segment.created_by.id !== this.user.id) {
                    this.createSegmentDo(segment);
                } else {
                    this.updateSegmentDo(segment);
                }
            },

            createSegmentDo(segment) {

                let form = new Form(segment);

                this.saveSegment(form)
                    .then(() => {
                        this.saveSegmentSuccess();
                        Bus.$emit('flash-message', {type: 'info', message: 'Segment saved!'});
                    })
                    .catch(error => {
                        if (error.status === 422) {

                            this.errorMessages = form.getAllErrors();

                            Bus.$emit('flash-message', {
                                type: 'error',
                                delay: 5000,
                                message: 'This segment cannot be saved. Make sure your segment is completed!'
                            });
                        } else if (error.status === 403) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'You are not authorized to save this segment!'
                            });
                        } else {
                            Bus.$emit('server-error');
                        }
                    });
            },

            updateSegmentDo(segment) {

                let form = new Form(segment);

                this.updateSegment(form)
                    .then(() => {
                        this.saveSegmentSuccess();
                        Bus.$emit('flash-message', {type: 'info', message: 'Segment updated!'});
                    })
                    .catch(error => {
                        if (error.status === 422) {

                            this.errorMessages = form.getAllErrors();

                            Bus.$emit('flash-message', {
                                type: 'error',
                                delay: 5000,
                                message: 'This segment cannot be updated. Make sure your segment is completed!'
                            });
                        } else if (error.status === 403) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'You are not authorized to update this segment!'
                            });
                        } else {
                            Bus.$emit('server-error');
                        }
                    });
            },

            segmentClosed() {
                this.$emit('close');
            },
        },

        created() {

            if (!this.$store.state.Segments) {
                this.$store.registerModule('Segments', Store);
            }
        },

        mounted() {
            this.loadSegments();
            this.getDataVariables();
        }
    }

</script>
